import React, { useEffect } from "react";
import { Formik } from "formik";
import { isEmpty } from "lodash";
import { SelectDropdown } from "@origo_vl_banking/ibanking-react";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import { format, strip } from "@origo_vl_banking/utils";
import * as Yup from 'yup';
import {
  Label,
  Line,
  RowWithBorders,
  TextRight,
  TwoColumns,
} from "../../../components/grid/styled-components";
import { normalizeMortgageItem, preventNumError } from "../../../utils/helpers";
import TextInput from "../../../components/text-input/TextInput";
import { useDispatch, useSelector } from "react-redux";
import { validate } from "../../../utils/validate";
import {
  calcConsolidation,
  getCarInfo,
  getCarInsuranceCompanies,
  setCarInsuranceCompany,
  setMilage,
  setNewCollateralAmount,
  setSellerSsnAndGetName,
  setSsn,
  setInitialDeposit,
  setAdditionalDeposit,
  setDepositPercentage,
} from "../actions";
import {
  NewCollateralGrid,
  InputWrapper,
  VehicleWrapper,
  VehicleType
} from "./styled-components";

const validationSchema = Yup.object().shape({
  purchasePrice: validate.formik.amount,
  collateralItem: validate.formik.required,
  ssn: validate.formik.required,
});

const NewCollateral = () => {
  const dispatch = useDispatch();
  const consolidation = useSelector((state) => state.consolidation);
  const { 
    newCollateral, 
    newCollateralNumber,
    collateralValue,
    initialDeposit,
    additionalCashDeposit,
    ssn: consolidationSsn,
    sellerName,
    carInsuranceCompanies,
    collateralMilage,
    productType
  } = consolidation;
  
  useEffect(() => {
    dispatch(getCarInsuranceCompanies());
  }, [dispatch]);

  const handleCarData = (value, handleBlur, e) => {
    handleBlur(e);
    if (newCollateralNumber === value) return;
    dispatch(getCarInfo(value));
  };

  const handleBlurPrice = (value, handleBlur, e) => {
    handleBlur(e);
    if (isEmpty(value)) return;
    
    const price = parseInt(strip.amount(value), 10);
    if (price < 0 || price === newCollateral?.salesAmt) return;
    
    dispatch(setNewCollateralAmount(price));
    
    if (price != 0) {
      const ownershipBalance = collateralValue || 0;
      
      const minDepositAmount = Math.round(price * 0.0);
      
      let targetDepositAmount;
      let targetPercentage;
      
      if (initialDeposit && newCollateral?.salesAmt) {
        const currentPercentage = Math.round((initialDeposit / newCollateral.salesAmt) * 100);
        const percentBasedDeposit = Math.round(price * (currentPercentage / 100));
        
        targetDepositAmount = Math.max(minDepositAmount, percentBasedDeposit, ownershipBalance);
        targetPercentage = Math.round((targetDepositAmount / price) * 100);
      } else {
        targetDepositAmount = Math.max(minDepositAmount, ownershipBalance);
        targetPercentage = Math.round((targetDepositAmount / price) * 100);
      }
      
      const additionalCash = Math.max(0, targetDepositAmount - ownershipBalance);
      
      dispatch(setInitialDeposit(targetDepositAmount));
      dispatch(setAdditionalDeposit(additionalCash));
      dispatch(setDepositPercentage(targetPercentage));
    }
    
    dispatch(calcConsolidation());
  };

  const handleMileageOnBlur = (value, handleBlur, e) => {
    handleBlur(e);
    const mileage = parseInt(strip.amount(value), 10);
    dispatch(setMilage(mileage));
  };

  const handleInsuranceSelect = (id) => {
    const selected = carInsuranceCompanies.find(item => item.key === id);
    if (selected) {
      dispatch(setCarInsuranceCompany(selected.key));
    }
  };

  const handleSellerSSN = (value, handleBlur, e) => {
    handleBlur(e);
    if (!value) return;
    
    const parsedValue = value.replace(/-/g, "");
    if (!parsedValue || !validate.ssn(parsedValue)) return;
    if (parsedValue === consolidationSsn) return;
    
    dispatch(setSellerSsnAndGetName(parsedValue));
  };

  const getInsuranceDisplayValue = () => {
    const id = newCollateral?.insuranceCompanyId;
    return carInsuranceCompanies.find(x => x.key === id)?.value || "";
  };

  const getInitialValues = () => ({
    purchasePrice: newCollateral?.salesAmt || "",
    ssn: consolidationSsn || "",
    collateralItem: newCollateralNumber || "",
    collateralMilage: newCollateralNumber ? collateralMilage : undefined,
  });

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={getInitialValues()}
      enableReinitialize
    >
      {({
        handleBlur,
        handleChange,
        setFieldValue,
        values,
        errors,
        touched,
      }) => (
        <>
          <NewCollateralGrid>
            <InputWrapper gridArea="purchasePrice">
              <TextInput
                label="Consolidation.PurchasePrice"
                normalize="amount"
                id="purchasePrice"
                name="purchasePrice"
                height="34px"
                align="right"
                star="red"
                autoFocus
                value={values.purchasePrice}
                onBlur={(e) => handleBlurPrice(e.target.value, handleBlur, e)}
                onChange={(e) => {
                  preventNumError(e, handleChange);                  
                  const cleanValue = e.target.value.replace(/\./g, '');
                  const numValue = Number(cleanValue);
                  
                  if (!isNaN(numValue) && numValue >= 0) {
                    setFieldValue('purchasePrice', numValue);
                  }
                }}
                disabled={!consolidation.editable}
                tabIndex="1"
                rightSideText="kr."
                error={touched.purchasePrice && errors.purchasePrice}
              />
            </InputWrapper>
            
            <InputWrapper gridArea="licensePlate">
              <TextInput
                label="Mortgage.Item"
                name="collateralItem"
                height="34px"
                maxLength={6}
                value={values.collateralItem.toUpperCase()}
                onBlur={(e) => handleCarData(e.target.value, handleBlur, e)}
                onChange={(e) => normalizeMortgageItem(e.target.value, setFieldValue, "collateralItem")}
                align="right"
                disabled={!consolidation.editable}
                tabIndex="1.1"
                error={touched.collateralItem && errors.collateralItem}
                star="red"
              />
            </InputWrapper>

            <InputWrapper gridArea="collateralItem">
              <VehicleWrapper>
                <Label>
                  <FormattedMessage id="Mortgage.Vehicle" />
                </Label>
                <VehicleType>
                  {newCollateral?.name ?? ""}
                </VehicleType>
              </VehicleWrapper>
            </InputWrapper>

            <InputWrapper gridArea="collateralMilage">
              <TextInput
                label="Mortgage.Milage"
                name="collateralMilage"
                height="34px"
                normalize="amount"
                maxLength={7}
                align="right"
                value={values.collateralMilage}
                onBlur={(e) => handleMileageOnBlur(e.target.value, handleBlur, e)}
                disabled={!consolidation.editable}
                tabIndex="1.2"
                rightSideText="km"
              />
            </InputWrapper>

            <InputWrapper gridArea="mortageRegistration">
              <div>
                <p style={{ marginTop: 0 }}>
                  <Label>
                    <FormattedMessage id="Mortgage.Model" />
                  </Label>
                </p>
                <span>
                  {newCollateral?.firstRegistrationDate &&
                    moment(newCollateral.firstRegistrationDate).format("DD.MM.YYYY")}
                </span>
              </div>
            </InputWrapper>

            <InputWrapper gridArea="seller">
              <TextInput
                label="Application.Seller"
                name="ssn"
                normalize="ssn"
                maxLength={11}
                height="34px"
                align="right"
                onBlur={(e) => handleSellerSSN(e.target.value, handleBlur, e)}
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue('ssn', e.target.value);
                }}
                value={values.ssn}
                error={touched.ssn && errors.ssn}
                disabled={!consolidation.editable}
                star="red"
              />
            </InputWrapper>

            <InputWrapper gridArea="sellerName">
              <VehicleWrapper>
                <Label>
                  
                </Label>
                <VehicleType>
                  {sellerName ?? ""}
                </VehicleType>
              </VehicleWrapper>
            </InputWrapper>

            <InputWrapper gridArea="insurance">
              <SelectDropdown
                disabled={!consolidation.editable}
                label="Mortgage.InsuranceCompany"
                headerHeight="34px"
                name="insuranceCompanyId"
                data={carInsuranceCompanies}
                onClicked={handleInsuranceSelect}
                tabIndex="1.3"
                displayValue={getInsuranceDisplayValue()}
                error={touched.insuranceCompanyId && errors.insuranceCompanyId}
              />
            </InputWrapper>
          </NewCollateralGrid>

          <TwoColumns>
            <Line />
            <Label>
              <FormattedMessage id="Application.AdministrationFee" />
            </Label>
            <TextRight>
              {format.amount(consolidation.administrationFee) || 0} kr.
            </TextRight>
            <Label>
              <FormattedMessage id={productType === "057" ? "Application.StartingFee" : "Application.BorrowingFee"} />
            </Label>
            <TextRight>
              {format.amount(consolidation.commitmentFee) || 0} kr.
            </TextRight>
            <Label>
              <FormattedMessage id={productType === "057" ? "Application.FinalFee" : "Application.RegistrationFee"} />
            </Label>
            <TextRight>
              {format.amount(productType === "057" ? consolidation.finalFee : consolidation.registrationFee) || 0} kr.
            </TextRight>
            <RowWithBorders>
              <TwoColumns style={{ fontWeight: "bold", height: "34px" }}>
                <FormattedMessage id="Application.TotalLoanAmount" />
                <TextRight>{format.amount(consolidation.loanNetAmount) || 0} kr.</TextRight>
              </TwoColumns>
            </RowWithBorders>
            <Label>
              <FormattedMessage id="Consolidation.PayoutAmount" />
            </Label>
            <TextRight>{format.amount(consolidation.paymentToSeller) || 0} kr.</TextRight>
          </TwoColumns>
        </>
      )}
    </Formik>
  );
};

export default NewCollateral;
